import { generatePath } from "react-router";

import i18nConfig from "../../../commons/i18n-config";
import { getLanguageFromLocale } from "../../../commons/libs/locale";
import type { Locale, TranslationKey } from "../../../commons/types/localization";
import { ROUTES } from "../routes";

import { decodeIdFromMatch } from "./products";

export const TOOL_ENTRY_FILE_QUERY_PARAM = "entryFile";

export const removeLeadingTrailingSlashesInPath = (path: string) => {
  return path.replace(/^\/|\/$/g, "");
};

export const removeTrailingSlashInPath = (path: string) => {
  return path.replace(/\/$/g, "");
};

export const joinUrl = (baseUrl: string, path: string[]) => {
  return [removeTrailingSlashInPath(baseUrl), ...path.map(p => removeLeadingTrailingSlashesInPath(p))].join("/");
};

const getToolEntryFilePathname = (entryFile: string) => entryFile.split(/[?#]/)[0];

export const isValidToolEntryFile = (entryFile: unknown): entryFile is string => {
  if (typeof entryFile !== "string") {
    return false;
  }

  const trimmedEntryFile = entryFile.trim();

  if (!trimmedEntryFile || trimmedEntryFile.startsWith("/") || trimmedEntryFile.includes("\\")) {
    return false;
  }

  if (/^[a-z][a-z0-9+.-]*:/i.test(trimmedEntryFile)) {
    return false;
  }

  try {
    // 1. Validate percent-encoding across the entire string (throws on invalid encoding, e.g. %zz)
    // and explicitly reject any decoded or physical backslashes anywhere
    const decodedFull = decodeURIComponent(trimmedEntryFile);
    if (decodedFull.includes("\\")) {
      return false;
    }

    // 2. Perform path traversal validation on the pathname segment
    const decodedPathname = decodeURIComponent(getToolEntryFilePathname(trimmedEntryFile));
    return !decodedPathname.split("/").includes("..");
  } catch {
    return false;
  }
};

export const buildToolPath = (toolKey: string, entryFile?: string): string => {
  const path = generatePath(ROUTES.TOOL.INDEX, { key: toolKey });

  if (!isValidToolEntryFile(entryFile)) {
    return path;
  }

  const searchParams = new URLSearchParams({ [TOOL_ENTRY_FILE_QUERY_PARAM]: entryFile.trim() });
  return `${path}?${searchParams.toString()}`;
};

export const getToolEntryFileFromSearch = (search: string): string | undefined => {
  const entryFile = new URLSearchParams(search).get(TOOL_ENTRY_FILE_QUERY_PARAM);
  return isValidToolEntryFile(entryFile) ? entryFile.trim() : undefined;
};

export const sanitizeToolEntryFile = <T extends string | Partial<Record<string, string>>>(
  entryFile: T | undefined
): T | undefined => {
  if (entryFile === undefined) {
    return undefined;
  }

  if (typeof entryFile === "string") {
    return (isValidToolEntryFile(entryFile) ? entryFile.trim() : undefined) as T | undefined;
  }

  if (typeof entryFile === "object" && entryFile !== null) {
    const sanitizedObj: Record<string, string> = {};
    for (const [key, val] of Object.entries(entryFile)) {
      if (isValidToolEntryFile(val)) {
        sanitizedObj[key] = val.trim();
      }
    }
    return (Object.keys(sanitizedObj).length > 0 ? sanitizedObj : undefined) as T | undefined;
  }

  return undefined;
};

/**
 * Resolves a (possibly localized) tool entry file to a single sanitized string for the given locale.
 *
 * Mirrors the i18next UI translation fallback chain (see `initI18n`):
 * active locale → language of the locale → fallback language (language of `i18nConfig.defaultLocale`).
 * Returns `undefined` if the source is missing or contains no valid entry for the chain, so callers
 * can fall through to the next entry file source instead of crashing.
 */
export const resolveLocalizedToolEntryFile = (
  entryFile: string | Partial<Record<TranslationKey, string>> | undefined,
  locale: Locale
): string | undefined => {
  const sanitizedEntryFile = sanitizeToolEntryFile(entryFile);

  if (sanitizedEntryFile === undefined || typeof sanitizedEntryFile === "string") {
    return sanitizedEntryFile;
  }

  const language = getLanguageFromLocale(locale);
  const fallbackLanguage = getLanguageFromLocale(i18nConfig.defaultLocale);

  return (
    sanitizedEntryFile[locale] ??
    (language ? sanitizedEntryFile[language] : undefined) ??
    (fallbackLanguage ? sanitizedEntryFile[fallbackLanguage] : undefined)
  );
};

type ProductDetailsRoute = (typeof ROUTES.PRODUCT_DETAILS)[keyof typeof ROUTES.PRODUCT_DETAILS];

export const buildProductPath = (
  path: ProductDetailsRoute,
  productSpecs: { productId: string; productContentKey?: string }
): string =>
  generatePath(path, {
    id: decodeIdFromMatch(productSpecs.productId),
    ...(productSpecs.productContentKey ? { productContentKey: decodeIdFromMatch(productSpecs.productContentKey) } : {})
  });
